import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useState, useEffect, useCallback, useMemo } from "react";
import { FileText, Image, Search } from "lucide-react";
import { ModalWithTabs, type Tab } from "@admin/components/ui/ModalWithTabs";
import {
  generateSeoTitle,
  generateSeoDescription,
  generateSeoKeywords,
} from "@shared/utils/seoUtils";
import { useCategoryOptions } from "@shared/hooks/useCategoryTree";
import {
  CategorySelect,
  CustomSelect,
  ImageUpload,
  RichTextEditor,
} from "@shared/components";
import type { UploadedImage } from "@shared/components/ImageUpload";
import type { ProductImage } from "@shared/types/product";

// Esquema de validación
const productFormSchema = z.object({
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .min(2, "El nombre debe tener al menos 2 caracteres")
    .max(100, "El nombre no puede exceder 100 caracteres"),
  categoryId: z
    .string()
    .min(1, "La categoría es requerida")
    .uuid("Debe ser un UUID válido"),
  description: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 5000,
      "La descripción no puede exceder 5000 caracteres"
    ),
  brand: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 50,
      "La marca no puede exceder 50 caracteres"
    ),
  unitOfMeasurement: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 20,
      "La unidad de medida no puede exceder 20 caracteres"
    ),
  outstanding: z.boolean().optional(),
  order: z.number().min(0, "El orden debe ser mayor o igual a 0").optional(),
  seoTitle: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 60,
      "El título SEO no puede exceder 60 caracteres"
    ),
  seoDescription: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 160,
      "La descripción SEO no puede exceder 160 caracteres"
    ),
  seoKeywords: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length <= 200,
      "Las palabras clave SEO no pueden exceder 200 caracteres"
    ),
});

type ProductFormData = z.infer<typeof productFormSchema>;

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ProductFormData) => void;
  initialData?: Partial<ProductFormData>;
  existingImages?: ProductImage[] | null; // Imágenes existentes del producto (para modo edición)
  isLoading?: boolean;
}

export function ProductFormModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  existingImages,
  isLoading = false,
}: ProductFormModalProps) {
  // Estados para control de auto-SEO
  const [autoSeoEnabled, setAutoSeoEnabled] = useState(true);
  const [autoTitle, setAutoTitle] = useState(true);
  const [autoDescription, setAutoDescription] = useState(true);
  const [autoKeywords, setAutoKeywords] = useState(true);

  // Obtener opciones de categorías
  const { options: categoryOptions, isLoading: categoriesLoading } =
    useCategoryOptions();

  // Opciones de unidades de medida (valores que espera el backend)
  const unitOptions = [
    { value: "ud.", label: "Unidad/Pieza (ud.)" },
    { value: "kg", label: "Kilogramos (kg)" },
    { value: "m³", label: "Metros cúbicos (m³)" },
    { value: "caja", label: "Caja" },
    { value: "m²", label: "Metros cuadrados (m²)" },
    { value: "m", label: "Metros lineales (m)" },
    { value: "rollo", label: "Rollo" },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
    control,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      categoryId: "",
      description: "",
      brand: "",
      unitOfMeasurement: "",
      outstanding: false,
      order: 0,
      seoTitle: "",
      seoDescription: "",
      seoKeywords: "",
    },
    mode: "onChange",
  });

  // Estado para imágenes subidas (Cloudinary)
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);

  // Resetear el formulario con initialData cuando abrimos en modo edición
  useEffect(() => {
    if (!isOpen) return;

    const defaults = {
      name: "",
      categoryId: "",
      description: "",
      brand: "",
      unitOfMeasurement: "",
      outstanding: false,
      order: 0,
      seoTitle: "",
      seoDescription: "",
      seoKeywords: "",
    } as ProductFormData;

    // Mezclamos defaults con initialData (si existe)
    reset({
      ...defaults,
      ...(initialData || {}),
    });

    // Convertir imágenes existentes del producto a formato UploadedImage
    if (existingImages && existingImages.length > 0) {
      const convertedImages: UploadedImage[] = existingImages.map(
        (img, index) => ({
          id: `existing-${img.publicId || img.url}-${index}`, // ID único basado en publicId o URL
          url: img.url,
          publicId: img.publicId || "", // publicId puede ser opcional
          fileName: img.alt || img.title || `Imagen ${index + 1}`, // Usar alt, title, o nombre genérico
          alt: img.alt || "", // Preservar el alt original si existe
        })
      );
      setUploadedImages(convertedImages);
    } else {
      // Si no hay imágenes existentes, limpiar el estado
      setUploadedImages([]);
    }
  }, [isOpen, initialData, existingImages, reset]);

  // Observar cambios en los campos que afectan SEO
  const watchedFields = watch(["name", "description", "brand"]);
  const [name, description, brand] = watchedFields;

  // Función para generar SEO automático con debounce
  const extractPlainText = useCallback((html?: string | null) => {
    if (!html) return "";
    return html
      .replace(/<\/?(p|div|br)\b[^>]*>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const generateAutoSeo = useCallback(() => {
    if (!autoSeoEnabled) return;

    if (autoTitle && name) {
      const seoTitle = generateSeoTitle({ name, brand });
      setValue("seoTitle", seoTitle);
    }

    if (autoDescription) {
      const plainDescription = extractPlainText(description);
      if (plainDescription) {
        const seoDescription = generateSeoDescription({
          description: plainDescription,
        });
        setValue("seoDescription", seoDescription);
      }
    }

    if (autoKeywords && (name || brand)) {
      const seoKeywords = generateSeoKeywords({
        name: name || "",
        brand: brand || "",
        categoryName: "", // TODO: obtener nombre de categoría por ID
        extra: ["cerámica", "construcción", "materiales"],
      });
      setValue("seoKeywords", seoKeywords);
    }
  }, [
    autoSeoEnabled,
    autoTitle,
    autoDescription,
    autoKeywords,
    name,
    description,
    brand,
    setValue,
    extractPlainText,
  ]);

  // Efecto con debounce para generar SEO automático
  useEffect(() => {
    const timeoutId = setTimeout(generateAutoSeo, 300);
    return () => clearTimeout(timeoutId);
  }, [generateAutoSeo]);

  // Manejar cambios manuales en campos SEO (desactivar auto para ese campo)
  const handleSeoFieldChange = (
    field: "seoTitle" | "seoDescription" | "seoKeywords"
  ) => {
    if (field === "seoTitle") setAutoTitle(false);
    if (field === "seoDescription") setAutoDescription(false);
    if (field === "seoKeywords") setAutoKeywords(false);
  };

  const handleFormSubmit = (data: ProductFormData) => {
    // Adjuntar imágenes subidas en el formato exacto que espera el backend
    // Formato: { url: string, alt: string, order: number }
    const payloadWithImages = {
      ...data,
      images: uploadedImages.map((img, index) => ({
        url: img.url,
        alt: img.alt || "", // Usar alt preservado o string vacío
        order: index,
      })),
    } as any;
    onSubmit(payloadWithImages);
    reset();
  };

  const handleClose = () => {
    reset();
    setUploadedImages([]);
    // Resetear estados de auto-SEO
    setAutoSeoEnabled(true);
    setAutoTitle(true);
    setAutoDescription(true);
    setAutoKeywords(true);
    onClose();
  };

  // Definir los tabs
  const tabs: Tab[] = [
    {
      id: "general",
      label: "General",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          {/* Campos básicos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Nombre del Producto <span className="text-red-500">*</span>
            </label>
            <input
              {...register("name")}
              type="text"
              id="name"
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                errors.name
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Ej: Baldosa Cerámica 30x30"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="categoryId"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Categoría <span className="text-red-500">*</span>
            </label>
            <CategorySelect
              options={categoryOptions}
              value={watch("categoryId") || ""}
              onChange={(value) => setValue("categoryId", value)}
              placeholder="Selecciona una categoría"
              disabled={categoriesLoading}
              loading={categoriesLoading}
              error={!!errors.categoryId}
            />
            {errors.categoryId && (
              <p className="mt-1 text-sm text-red-600">
                {errors.categoryId.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="brand"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Marca
            </label>
            <input
              {...register("brand")}
              type="text"
              id="brand"
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                errors.brand
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Ej: Gassull"
            />
            {errors.brand && (
              <p className="mt-1 text-sm text-red-600">
                {errors.brand.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="unitOfMeasurement"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Unidad de Medida
            </label>
            <CustomSelect
              options={unitOptions}
              value={watch("unitOfMeasurement") || ""}
              onChange={(value) => setValue("unitOfMeasurement", value)}
              placeholder="Selecciona una unidad"
              searchable={false}
              error={!!errors.unitOfMeasurement}
            />
            {errors.unitOfMeasurement && (
              <p className="mt-1 text-sm text-red-600">
                {errors.unitOfMeasurement.message}
              </p>
            )}
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Descripción
            </label>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <RichTextEditor
                  value={field.value ?? ""}
                  onChange={(html) => field.onChange(html)}
                  placeholder="Describe el producto con formato enriquecido..."
                  minHeight="320px"
                  className="border border-gray-200"
                />
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Configuración */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="order"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Orden
              </label>
              <input
                {...register("order", { valueAsNumber: true })}
                type="number"
                id="order"
                min="0"
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                  errors.order
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="0"
              />
              {errors.order && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.order.message}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2 pt-8">
              <input
                {...register("outstanding")}
                type="checkbox"
                id="outstanding"
                className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
              />
              <label htmlFor="outstanding" className="text-sm text-gray-700">
                Producto destacado
              </label>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "images",
      label: "Imágenes",
      icon: <Image className="w-4 h-4" />,
      content: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imágenes del producto
            </label>
            <ImageUpload
              uploadedImages={uploadedImages}
              onImagesUploaded={setUploadedImages}
              onImageRemoved={(imageId) =>
                setUploadedImages((prev) =>
                  prev.filter((img) => img.id !== imageId)
                )
              }
              maxFiles={10}
              maxSizeMb={10}
            />
          </div>
        </div>
      ),
    },
    {
      id: "seo",
      label: "SEO",
      icon: <Search className="w-4 h-4" />,
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-gray-900">
              Configuración SEO
            </h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoSeo"
                checked={autoSeoEnabled}
                onChange={(e) => setAutoSeoEnabled(e.target.checked)}
                className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
              />
              <label htmlFor="autoSeo" className="text-sm text-gray-700">
                Generar SEO automáticamente
              </label>
            </div>
          </div>

          <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="seoTitle"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Título SEO
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoTitle"
                      checked={autoTitle}
                      onChange={(e) => setAutoTitle(e.target.checked)}
                      className="h-3 w-3 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
                    />
                    <label
                      htmlFor="autoTitle"
                      className="text-xs text-gray-500"
                    >
                      Auto
                    </label>
                  </div>
                </div>
                <input
                  {...register("seoTitle")}
                  type="text"
                  id="seoTitle"
                  maxLength={60}
                  onChange={(e) => {
                    register("seoTitle").onChange(e);
                    handleSeoFieldChange("seoTitle");
                  }}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                    errors.seoTitle
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Título optimizado para SEO (máx. 60 caracteres)"
                />
                <div className="flex justify-between mt-1">
                  {errors.seoTitle && (
                    <p className="text-sm text-red-600">
                      {errors.seoTitle.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {watch("seoTitle")?.length || 0}/60
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="seoDescription"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Descripción SEO
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoDescription"
                      checked={autoDescription}
                      onChange={(e) => setAutoDescription(e.target.checked)}
                      className="h-3 w-3 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
                    />
                    <label
                      htmlFor="autoDescription"
                      className="text-xs text-gray-500"
                    >
                      Auto
                    </label>
                  </div>
                </div>
                <input
                  {...register("seoDescription")}
                  type="text"
                  id="seoDescription"
                  maxLength={160}
                  onChange={(e) => {
                    register("seoDescription").onChange(e);
                    handleSeoFieldChange("seoDescription");
                  }}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                    errors.seoDescription
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Descripción para motores de búsqueda (máx. 160 caracteres)"
                />
                <div className="flex justify-between mt-1">
                  {errors.seoDescription && (
                    <p className="text-sm text-red-600">
                      {errors.seoDescription.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {watch("seoDescription")?.length || 0}/160
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="seoKeywords"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Palabras Clave SEO
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoKeywords"
                      checked={autoKeywords}
                      onChange={(e) => setAutoKeywords(e.target.checked)}
                      className="h-3 w-3 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
                    />
                    <label
                      htmlFor="autoKeywords"
                      className="text-xs text-gray-500"
                    >
                      Auto
                    </label>
                  </div>
                </div>
                <input
                  {...register("seoKeywords")}
                  type="text"
                  id="seoKeywords"
                  maxLength={200}
                  onChange={(e) => {
                    register("seoKeywords").onChange(e);
                    handleSeoFieldChange("seoKeywords");
                  }}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] [&:-webkit-autofill]:-webkit-text-fill-color-black ${
                    errors.seoKeywords
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="cerámica, baldosa, gres, porcelanato (separadas por comas)"
                />
                <div className="flex justify-between mt-1">
                  {errors.seoKeywords && (
                    <p className="text-sm text-red-600">
                      {errors.seoKeywords.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 ml-auto">
                    {watch("seoKeywords")?.length || 0}/200
                  </p>
                </div>
              </div>
          </div>
        </div>
      ),
    },
  ];

  // Determinar qué tabs tienen errores
  const tabErrors = useMemo(() => {
    const generalFields = [
      "name",
      "categoryId",
      "description",
      "brand",
      "unitOfMeasurement",
      "order",
      "outstanding",
    ];
    const seoFields = ["seoTitle", "seoDescription", "seoKeywords"];

    const hasGeneralErrors = generalFields.some((field) => errors[field as keyof typeof errors]);
    const hasSeoErrors = seoFields.some((field) => errors[field as keyof typeof errors]);

    return {
      general: hasGeneralErrors,
      images: false, // Las imágenes no tienen validación del formulario
      seo: hasSeoErrors,
    };
  }, [errors]);

  // Footer con botones (se mantiene fuera de los tabs)
  const footer = (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={handleClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors"
        disabled={isLoading}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="product-form"
        disabled={!isValid || isLoading}
        className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {initialData?.name ? "Actualizando..." : "Creando..."}
          </span>
        ) : initialData?.name ? (
          "Actualizar Producto"
        ) : (
          "Crear Producto"
        )}
      </button>
    </div>
  );

  return (
    <ModalWithTabs
      isOpen={isOpen}
      onClose={handleClose}
      title={initialData?.name ? "Editar Producto" : "Crear Nuevo Producto"}
      tabs={tabs}
      size="xl"
      footer={footer}
      defaultTab="general"
      formId="product-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      tabErrors={tabErrors}
    />
  );
}
